import { useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCircle } from 'lucide-react';

import {
  Notification,
  useGetAllNotificationsQuery,
  useUpdateReadStatusMutation,
} from '@/store/api/notificationApi';
import { BackButton } from '@/components/common/BackButton';

const TAKE = 10;

export default function NotificationsPage() {
  const [skip, setSkip] = useState(0);
  const [items, setItems] = useState<Notification[]>([]);

  const { data, isLoading, isFetching } = useGetAllNotificationsQuery({
    skip,
    take: TAKE,
  });
  const [updateReadStatus, { isLoading: isUpdating }] = useUpdateReadStatusMutation();

  const loaderRef = useRef<HTMLDivElement | null>(null);

  const totalCount = data?.count ?? 0;
  const hasMore = items.length < totalCount;

  /* Append data */
  useEffect(() => {
    if (data?.data?.length) {
      setItems((prev: any) => {
        const existingIds = new Set(prev.map((n: any) => n.id));
        const newItems = data.data.filter((n) => !existingIds.has(n.id));
        return [...prev, ...newItems];
      });
    }
  }, [data]);

  /* Infinite scroll observer */
  useEffect(() => {
    if (!hasMore || isFetching) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setSkip((prev) => prev + TAKE);
        }
      },
      { threshold: 1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isFetching]);

  const handleReadNotif = async (id: number) => {
    if (!id) return;

    await updateReadStatus({
      id: id,
    }).unwrap();
  };

  return (
    <div className="p-6 max-w-4xl text-foreground">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <BackButton />
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-semibold">Notifications</h1>
        </div>
      </div>

      {/* Empty state */}
      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <Bell className="h-10 w-10 mb-3 opacity-50" />
          <p className="text-sm font-medium">No notifications found</p>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {items.map((item) => {
          const isViewed = !!item.viewed;

          return (
            <div
              key={item.id}
              className={`
        flex gap-4 p-4 rounded-xl border
        bg-card text-card-foreground
        border-border
        transition hover:bg-accent
        ${!isViewed ? 'ring-1 ring-primary/30' : ''}
      `}
            >
              {/* Icon */}
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Bell className="h-5 w-5 text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <p className="text-sm font-semibold">{item.title}</p>
                    <p className="text-sm text-muted-foreground mt-1">{item.body}</p>
                  </div>

                  <div className="flex items-center pt-1">
                    {!isViewed ? (
                      <button
                        onClick={() => handleReadNotif(Number(item.id))}
                        disabled={isUpdating}
                        className="
        flex items-center gap-1.5
        rounded-full border border-primary/30
        bg-primary/10 px-3 py-1.5
        text-xs font-medium text-primary
        hover:bg-primary/20
        disabled:opacity-50
        transition
      "
                      >
                        <Check className="h-3.5 w-3.5" />
                        Mark as read
                      </button>
                    ) : (
                      <div
                        className="
        flex items-center gap-1.5
        rounded-full border border-green-500/30
        bg-green-500/10 px-3 py-1.5
        text-xs font-medium text-green-600
      "
                      >
                        <CheckCircle className="h-3.5 w-3.5" />
                        Read
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground mt-2">
                  {new Date(item.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <div ref={loaderRef} className="flex justify-center py-6 text-sm text-muted-foreground">
          {isFetching ? 'Loading more notifications…' : ''}
        </div>
      )}

      {!hasMore && !isLoading && (
        <p className="text-center text-xs text-muted-foreground mt-6">You’ve reached the end</p>
      )}
    </div>
  );
}
